import type { ZodIssue } from 'zod';

type WorkOrderValues = { type?: string | null } & Record<string, unknown>;
type PsFormValues = { workOrders?: WorkOrderValues[] | null } & Record<string, unknown>;

/** Client Details + Case Details fields in on-screen order. Keys are full form paths. */
const CLIENT_CASE_FIELD_ORDER: { key: string; label: string }[] = [
  { key: 'type', label: 'Service Type' },
  { key: 'clientId', label: 'Client #' },
  { key: 'clientName', label: 'Client Name' },
  { key: 'contactId', label: "Client's Contact" },
  { key: 'case.attorneyId', label: 'Attorney’s Name' },
  { key: 'process.represents', label: 'Represents' },
  { key: 'case.jurisdiction', label: 'Jurisdiction' },
  { key: 'case.typeId', label: 'Case Type' },
  { key: 'case.courtId', label: 'Court Name' },
  { key: 'case.number', label: 'Case #' },
  { key: 'case.name', label: 'Case Name' },
  { key: 'case.fileNo', label: 'File #' },
  { key: 'case.plaintiff', label: 'Plaintiff' },
  { key: 'case.defendant', label: 'Defendant' },
  { key: 'case.address1', label: 'Property Address Line 1' },
  { key: 'case.address2', label: 'Property Address Line 2' },
  { key: 'case.city', label: 'City' },
  { key: 'case.state', label: 'State' },
  { key: 'case.zipCode', label: 'Zip Code' },
  { key: 'case.serveBy', label: 'Serve By' },
  { key: 'case.hearingOn', label: 'Hearing' },
  { key: 'case.rush', label: 'Rush' },
  { key: 'isPrepaid', label: 'Pre-paid' },
  { key: 'case.hasInsurance', label: 'Insurance Data' },
  { key: 'case.insurance.adjuster', label: 'Adjuster' },
  { key: 'case.insurance.adjusterName', label: "Adjuster's Name" },
  { key: 'case.insurance.insuredName', label: 'Insured Name' },
  { key: 'case.insurance.phone', label: 'Phone Number' },
  { key: 'case.insurance.fax', label: 'Fax' },
  { key: 'case.insurance.dateOfLoss', label: 'Date of Loss' },
  { key: 'case.insurance.billTo', label: 'Bill To' },
  { key: 'case.insurance.policy', label: 'Policy' },
  { key: 'case.insurance.claim', label: 'Claim' },
  // Whole-section issues (only while a step is still mounting and its object is not registered yet).
  { key: 'process', label: 'Represents' },
  { key: 'case', label: 'Case Details' },
  { key: 'workOrders', label: 'W/O Details' },
];

/** W/O form fields in on-screen order. Keys are issue paths below `workOrders[i]`, with array indices removed. */
const WO_FIELD_ORDER: { key: string; label: string }[] = [
  { key: 'type', label: 'Type' },
  { key: 'toServe', label: 'Person to Serve' },
  { key: 'addressType', label: 'Address Type' },
  { key: 'address1', label: 'Address Line 1' },
  { key: 'address2', label: 'Address Line 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipCode', label: 'Zip Code' },
  { key: 'phone', label: 'Phone Number' },
  { key: 'privateInfo', label: 'Private Information' },
  { key: 'privateInfo.ssn', label: 'Social Security Number' },
  { key: 'privateInfo.dateOfBirth', label: 'Date of Birth' },
  { key: 'privateInfo.dlNumber', label: 'Driver License' },
  { key: 'witnessFee.issueDate', label: 'Witness Fee Date' },
  { key: 'witnessFee.issuedTo', label: 'Witness Fee Issued To' },
  { key: 'witnessFee.checkNumber', label: 'Witness Fee Check #' },
  { key: 'witnessFee.amount', label: 'Witness Fee Amount' },
  { key: 'affidavitTypeId', label: 'Affidavit Type' },
  { key: 'serveTo', label: 'Assign To' },
  { key: 'vendorDetails.department', label: 'Department' },
  { key: 'vendorDetails.vendorId', label: 'Vendor Name' },
  { key: 'vendorDetails.action', label: 'Service Action' },
  { key: 'vendorDetails.otherAction', label: 'Other Service Action' },
  { key: 'vendorDetails.afterAction', label: 'After Service Action' },
  { key: 'vendorDetails.otherAfterAction', label: 'Other After Service Action' },
  { key: 'vendorDetails.amount', label: 'Vendor Amount' },
  { key: 'serverId', label: 'Server Name' },
  { key: 'serverInstructions', label: 'Special Instructions' },
  { key: 'documents', label: 'Description of Documents' },
  { key: 'teamId', label: 'Assign Team' },
  { key: 'userId', label: 'Assign User' },
  { key: 'payments.issueDate', label: 'Check Date' },
  { key: 'payments.issuedTo', label: 'Check Issued To' },
  { key: 'payments.checkNumber', label: 'Check #' },
  { key: 'payments.amount', label: 'Check Amount' },
];

const humanize = (key: string) => key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

const getAtPath = (values: unknown, path: (string | number)[]) =>
  path.reduce<unknown>(
    (acc, segment) => (acc && typeof acc === 'object' ? (acc as Record<string | number, unknown>)[segment] : undefined),
    values
  );

const isEmptyValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

/**
 * Builds the summary shown under the disabled Save button, e.g.
 * "Person to Serve + 1 field are required." / "Zip Code is invalid." / "Type + 2 fields need attention."
 * Returns null when there are no issues.
 */
export const getPsSaveValidationMessage = (issues: ZodIssue[], values: PsFormValues): string | null => {
  const seen = new Set<string>();
  const fields = issues
    // Some superRefine issues use dotted single-segment paths (e.g. 'case.jurisdiction'); normalise them.
    .map((issue) => issue.path.flatMap((segment) => (typeof segment === 'string' ? segment.split('.') : [segment])))
    .map((path, index) => ({ path, message: issues[index].message }))
    .filter(({ path }) => {
      const id = path.join('.');
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map(({ path, message }) => {
      const isWorkOrderField = path[0] === 'workOrders' && typeof path[1] === 'number';
      const woIndex = isWorkOrderField ? (path[1] as number) : 0;
      const rest = isWorkOrderField ? path.slice(2) : path;
      const key = rest.filter((segment) => typeof segment === 'string').join('.');
      const rowIndex = rest.find((segment): segment is number => typeof segment === 'number') ?? 0;
      const fieldOrder = isWorkOrderField ? WO_FIELD_ORDER : CLIENT_CASE_FIELD_ORDER;
      const order = fieldOrder.findIndex((field) => field.key === key);
      const workOrder = isWorkOrderField ? values.workOrders?.[woIndex] : undefined;

      let label = order === -1 ? humanize(String(rest[rest.length - 1] ?? 'Field')) : fieldOrder[order].label;
      if (isWorkOrderField && key === 'toServe' && workOrder?.type === 'BUSINESS') label = 'Business Name';

      const missing = /required/i.test(message) || isEmptyValue(getAtPath(values, path));

      return {
        label,
        missing,
        sortKey: [isWorkOrderField ? 1 : 0, woIndex, order === -1 ? fieldOrder.length : order, rowIndex],
      };
    })
    .sort((a, b) => a.sortKey.reduce((diff, value, i) => diff || value - b.sortKey[i], 0));

  if (!fields.length) return null;

  const [first] = fields;
  const remaining = fields.length - 1;
  const subject = remaining ? `${first.label} + ${remaining} ${remaining === 1 ? 'field' : 'fields'}` : first.label;
  const missingCount = fields.filter((field) => field.missing).length;

  if (missingCount === fields.length) return `${subject} ${remaining ? 'are' : 'is'} required.`;
  if (missingCount === 0) return `${subject} ${remaining ? 'are' : 'is'} invalid.`;
  return `${subject} need attention.`;
};
